// src/l1packs/english.js
// Source: "Norwegian speech sounds compared to English" (Olaf Husby, NTNU NoW)
//
// Normalizations:
// - Use canonical IPA /ʋ/ for Norwegian <v> (PDF sometimes writes /v/).
//
// Visualization tags vocabulary (consonants)
// place:* => lips | teeth | ridge | palate | velum
// air:*   => burst | noisy | smooth
// sec:*   => palatalized | velarized
// phon:*  => voiced | devoice-final
// hint:*  => less-aspirated-*  (optional)
//
// Added for UI:
// - easyComment: short, beginner-friendly note (used in Easy mode)
// - userComment: fuller learner-facing explanation (Detailed mode)
// - difficulty: "ok" | "watch" | "new" (optional UI chip; use mostly for red sounds)
// - keywords: ["..."] (for red sounds; used for search/filters + future visualization hooks)
// - visVowel: ONLY when note is about vowel QUALITY (front/central/back, close/mid/open, rounding).
//            If note is only LENGTH (shorter/longer), keep visVowel undefined so no vowel diagram shows.

export const ENGLISH_PACK = {
  meta: {
    code: "en",
    name: "English",
    source: "NTNU NoW – Norwegian speech sounds compared to English (Olaf Husby)",
  },

  byIpa: {
    // ------------------------------- Consonants -------------------------------

    p: {
      overlap: "+",
      no: ["på"],
      l1: ["paw"],
      comment: "",
      easyComment: "Same idea as English /p/ (just don’t overdo the puff).",
      userComment:
        "Bilabial stop. Lips close fully and release with a small burst of air. English /p/ and Norwegian /p/ are close.",
      vis: { tags: ["place:lips", "air:burst"] },
    },

    b: {
      overlap: "+",
      no: ["bi"],
      l1: ["be"],
      comment: "",
      easyComment: "Same as English /b/.",
      userComment:
        "Bilabial voiced stop. Lips close and release; vocal folds vibrate. English /b/ and Norwegian /b/ are close.",
      vis: { tags: ["place:lips", "phon:voiced"] },
    },

    t: {
      overlap: "~",
      no: ["tå"],
      l1: ["taw"],
      comment: "NO: dental; ENG: alveolar",
      easyComment: "Norwegian /t/ is usually a bit further forward (touch the teeth).",
      userComment:
        "Main difference is place: Norwegian /t/ is often dental (tongue against the teeth), while English /t/ is usually alveolar (tongue on the ridge). Aim a bit more forward for Norwegian.",
      visCompare: {
        no: { tags: ["place:teeth", "air:burst"] }, // NO dental
        l1: { tags: ["place:ridge", "air:burst"] }, // EN alveolar
      },
    },

    d: {
      overlap: "~",
      no: ["deg"],
      l1: ["day"],
      comment: "NO: dental; ENG: alveolar",
      easyComment: "Norwegian /d/ is also a bit forward (toward the teeth).",
      userComment:
        "Main difference is place: Norwegian /d/ is often dental (teeth), while English /d/ is usually alveolar (ridge). For Norwegian, keep the tongue slightly more forward.",
      visCompare: {
        no: { tags: ["place:teeth", "phon:voiced"] },
        l1: { tags: ["place:ridge", "phon:voiced"] },
      },
    },

    "ʈ": {
      overlap: "-",
      no: ["smart"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["retroflex", "tongue-tip-curl", "cluster-assimilation", "rd/rt/rn/rl"],
      easyComment:
        "NEW for most Brits: a “rolled-back” T sound, often after /r/ (rt/rd/rn/rl).",
      userComment:
        "Retroflex stop (Norwegian). English has no direct equivalent. It often appears after /r/ (spelling like rt/rd). Tongue tip curls slightly back; the contact is behind the usual English /t/ place. Start by saying an /r/ then slide into a /t/ with the tip slightly curled back.",
      // No vis: our landmarks don’t model retroflex well yet (future: add a retroflex landmark or a “curl” overlay)
    },

    "ɖ": {
      overlap: "-",
      no: ["ferdig"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["retroflex", "voiced-stop", "tongue-tip-curl", "rd"],
      easyComment:
        "NEW for most Brits: a “rolled-back” D sound, often in /rd/.",
      userComment:
        "Retroflex voiced stop (Norwegian). No direct English equivalent. Often arises from /r/ + /d/ sequences. Tongue tip curls slightly back; keep voicing on.",
    },

    k: {
      overlap: "+",
      no: ["kåt"],
      l1: ["caught"],
      comment: "",
      easyComment: "Same /k/ idea as English.",
      userComment:
        "Velar stop. Back of tongue closes at the soft palate (velum) and releases. English /k/ and Norwegian /k/ are close.",
      vis: { tags: ["place:velum", "air:burst"] },
    },

    "ɡ": {
      overlap: "+",
      no: ["glass"],
      l1: ["glass"],
      comment: "",
      easyComment: "Same /g/ idea as English.",
      userComment:
        "Velar voiced stop. Back of tongue closes at the soft palate; vocal folds vibrate. Similar in English and Norwegian.",
      vis: { tags: ["place:velum", "phon:voiced"] },
    },

    m: {
      overlap: "+",
      no: ["min"],
      l1: ["mean"],
      comment: "",
      easyComment: "Same /m/.",
      userComment:
        "Bilabial nasal. Lips close; air goes through the nose. Similar in both languages.",
      vis: { tags: ["place:lips", "air:smooth"] },
    },

    n: {
      overlap: "+",
      no: ["nei"],
      l1: ["nay"],
      comment: "",
      easyComment: "Same /n/.",
      userComment:
        "Alveolar nasal. Tongue touches the ridge; air goes through the nose. Similar in both languages.",
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    "ɳ": {
      overlap: "-",
      no: ["barna"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["retroflex", "nasal", "rn"],
      easyComment:
        "NEW: a “rolled-back” N, often from /rn/.",
      userComment:
        "Retroflex nasal (Norwegian). No direct English equivalent. Often comes from /r/ + /n/ (rn). Tongue tip curls slightly back; keep nasal airflow.",
    },

    "ŋ": {
      overlap: "+",
      no: ["ring"],
      l1: ["ring"],
      comment: "",
      easyComment: "Same /ng/ sound as in English “sing”.",
      userComment:
        "Velar nasal. Back of tongue is raised toward the soft palate; air flows through the nose. Similar in both languages.",
      vis: { tags: ["place:velum", "air:smooth"] },
    },

    f: {
      overlap: "+",
      no: ["fiks"],
      l1: ["fix"],
      comment: "",
      easyComment: "Same /f/.",
      userComment:
        "Fricative at the teeth/lip area. Narrow gap creates noisy airflow. Similar in both languages.",
      vis: { tags: ["place:teeth", "air:noisy"] },
    },

    "ʋ": {
      overlap: "~",
      no: ["vest"],
      l1: ["vest"],
      comment: "ENG /v/ is more fricative",
      easyComment:
        "Norwegian v is softer: more like a smooth glide than English “v”.",
      userComment:
        "Norwegian /ʋ/ is usually a smooth approximant (less noise), while English /v/ is typically a stronger fricative (more noise). Don’t over-fricate in Norwegian.",
      visCompare: {
        no: { tags: ["place:teeth", "air:smooth"] }, // NO /ʋ/
        l1: { tags: ["place:teeth", "air:noisy"] },  // EN /v/
      },
    },

    s: {
      overlap: "+",
      no: ["si"],
      l1: ["see"],
      comment: "",
      easyComment: "Same /s/.",
      userComment:
        "Alveolar fricative. Tongue near the ridge creates noisy airflow. Similar in both languages.",
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ʃ": {
      overlap: "+",
      no: ["ski"],
      l1: ["she"],
      comment: "",
      easyComment: "Same “sh” sound.",
      userComment:
        "Postalveolar-ish fricative. This diagram doesn’t have a separate ‘postalveolar’ landmark, so it’s shown near the ridge with noisy airflow. Functionally similar in both languages.",
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ç": {
      overlap: "-",
      no: ["kjapp"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["palatal-fricative", "kj", "front-tongue", "hissy-soft"],
      easyComment:
        "NEW: the Norwegian “kj” sound — a soft hiss with the tongue high and front.",
      userComment:
        "Palatal fricative (Norwegian /kj/). English doesn’t have a direct equivalent. Keep the tongue body high and front (near the hard palate) and let air hiss through a narrow gap. It’s ‘softer’ than English /ʃ/ and usually produced further forward.",
      // FIX: use place:palate (not place:palatalized)
      vis: { tags: ["place:palatepalatalized", "air:noisy"] },
    },

    j: {
      overlap: "+",
      no: ["jet"],
      l1: ["yet"],
      comment: "",
      easyComment: "Same /y/ glide as in “yes”.",
      userComment:
        "Palatal glide. Tongue body raised toward the hard palate with smooth airflow. Similar in both languages.",
      // FIX: use place:palate (not place:palatalized)
      vis: { tags: ["place:palatalized", "air:smooth"] },
    },

    h: {
      overlap: "+",
      no: ["hi"],
      l1: ["he"],
      comment: "",
      easyComment: "Same /h/ idea.",
      userComment:
        "Glottal-like fricative/aspiration. The mouth shape depends on the following vowel. Similar in both languages.",
      // we don’t have “glottis”; keep airflow hint only
      vis: { tags: ["air:noisy"] },
    },

    r: {
      overlap: "~",
      no: ["rein", "verre"],
      l1: ["rain", "very"],
      comment: "ENG: /r/ is approximant; NO can be tap/trill (dialectal variation)",
      easyComment:
        "Norwegian r can be tapped/rolled — don’t do a strong English ‘r’ colouring.",
      userComment:
        "English /r/ is usually an approximant, often with ‘r-colouring’. Norwegian /r/ varies by dialect (tap/trill or uvular). If you’re aiming for a tap/trill style, avoid the heavy English ‘r’ posture; keep it lighter and quicker.",
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    l: {
      overlap: "~",
      no: ["lin"],
      l1: ["lean"],
      comment: "EN often has dark /l/ (velarized) in many positions; NO is typically lighter",
      easyComment:
        "Norwegian l is usually lighter; avoid the dark ‘full’ /l/ posture.",
      userComment:
        "Norwegian /l/ is often ‘lighter’. English frequently has a velarized (‘dark’) /l/, especially at the end of syllables. For Norwegian, avoid pulling the tongue back too much.",
      visCompare: {
        no: { tags: ["place:ridge", "air:smooth"] },
        l1: { tags: ["place:ridge", "air:smooth", "sec:velarized"] },
      },
    },

    "ɭ": {
      overlap: "-",
      no: ["jarl"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["retroflex", "lateral", "rl"],
      easyComment:
        "NEW: a “rolled-back” L, often from /rl/.",
      userComment:
        "Retroflex lateral (Norwegian). English doesn’t have a direct equivalent. Often comes from /r/ + /l/ (rl). Tongue tip curls back slightly; airflow stays along the sides (lateral).",
    },

    // --------------------------------- Vowels ---------------------------------
    // Strategy for British learners:
    // - Add easyComment everywhere (1-liner).
    // - Mark truly “new” (red) vowels as difficulty:"new" with keywords.
    // - Only add visVowel when you want a vowel diagram. (Kept minimal here.)

    i: {
      overlap: "~",
      no: ["sinn"],
      l1: ["sin"],
      comment: "",
      easyComment: "Close to English “i” in “sin”, but keep it clean/steady.",
      userComment:
        "Near-close front vowel. Similar category, but exact quality differs by accent. Norwegian often sounds ‘cleaner’/less diphthongized in careful speech.",
    },

    "iː": {
      overlap: "+",
      no: ["mi"],
      l1: ["me"],
      comment: "",
      easyComment: "Very close to English long ‘ee’.",
      userComment:
        "Close front long vowel. Very close match for many English accents.",
    },

    y: {
      overlap: "-",
      no: ["synd"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["front-rounded", "no-english-equivalent", "whistle-lips", "like-ü"],
      easyComment:
        "NEW: Norwegian /y/ = ‘ee’ tongue + rounded lips (like German ü).",
      userComment:
        "Front rounded vowel (no direct English equivalent). Start from English /i/ (‘ee’) and keep the tongue position, then round/purse the lips. Don’t move the tongue back (that turns it into /u/).",
    },

    "yː": {
      overlap: "-",
      no: ["lys"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["front-rounded", "long-vowel", "ü-long"],
      easyComment:
        "NEW: long version of /y/ — hold it steady.",
      userComment:
        "Long front rounded vowel. Same target as /y/ but longer and steadier. Avoid drifting into an English /uː/ quality.",
    },

    e: {
      overlap: "+",
      no: ["send"],
      l1: ["send"],
      comment: "",
      easyComment: "Often a good match with English ‘e’ in “send”.",
      userComment:
        "Mid/front vowel. Often a good match across languages here.",
    },

    "eː": {
      overlap: "~",
      no: ["ler"],
      l1: ["lair"],
      comment: "NO is steady; EN often diphthongizes in similar words",
      easyComment:
        "Keep Norwegian /eː/ steady (don’t glide like some English vowels).",
      userComment:
        "Norwegian /eː/ is a long monophthong. English often has movement (diphthongization) in similar words. Keep it steadier in Norwegian.",
    },

    "ø": {
      overlap: "-",
      no: ["søtt"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["front-rounded", "mid-vowel", "no-english-equivalent", "like-eu"],
      easyComment:
        "NEW: /ø/ = ‘eh’ tongue + rounded lips.",
      userComment:
        "Front rounded vowel (no direct English equivalent). Start from something like /e/ (‘eh’) and round the lips while keeping the tongue fairly front. Avoid sliding toward /o/ (too back).",
    },

    "øː": {
      overlap: "-",
      no: ["møt"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["front-rounded", "long-vowel", "ø-long"],
      easyComment:
        "NEW: long /øː/ — hold it steady and rounded.",
      userComment:
        "Long front rounded vowel. Keep the vowel steady (no glide) and keep the tongue front while rounding the lips.",
    },

    "æ": {
      overlap: "~",
      no: ["vært"],
      l1: ["daddy"],
      comment: "English ‘a’ varies a lot; treat as approximate",
      easyComment:
        "Roughly like ‘a’ in some ‘cat’/‘dad’ areas, but aim for a stable Norwegian target.",
      userComment:
        "Open/front-ish vowel. English ‘a’ varies a lot by accent; Norwegian target is typically more stable. Treat as an approximate match and tune by listening.",
    },

    "æː": {
      overlap: "~",
      no: ["mædi"],
      l1: ["mad"],
      comment: "",
      easyComment:
        "Same quality as /æ/ but longer; keep it steady.",
      userComment:
        "Longer /æ/-quality vowel. English ‘mad’ is short; Norwegian may hold it longer and keep it steadier.",
    },

    "ɑ": {
      overlap: "-",
      no: ["ta"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["open-back", "a-vowel", "not-trap", "not-schwa"],
      easyComment:
        "Often NEW-ish: a clear open ‘a’ target (don’t reduce it).",
      userComment:
        "Open back vowel target marked as no close English equivalent in this table. English learners often map it inconsistently (TRAP/START/STRUT). In Norwegian, keep it full (not reduced), with a low tongue and relatively back quality depending on dialect.",
    },

    "ɑː": {
      overlap: "~",
      no: ["spa"],
      l1: ["spa"],
      comment: "",
      easyComment: "Often close to ‘spa’, but keep it clean and long.",
      userComment:
        "Open back long vowel. Similar in many accents, but English may color it differently. Norwegian aims for a cleaner long vowel.",
    },

    "ʉ": {
      overlap: "~",
      no: ["full"],
      l1: ["full"],
      comment: "Norwegian is often more central than English /ʊ/",
      easyComment:
        "Like ‘u’ in “full”, but Norwegian is often a bit more central/rounded.",
      userComment:
        "Central rounded-ish vowel in Norwegian. English ‘full’ varies by accent; treat as an approximate match and refine by listening. Avoid turning it into a front /y/ or a back /u/.",
      // If you later want a vowel diagram, you can add visVowel hints here.
    },

    "ʉː": {
      overlap: "~",
      no: ["tur"],
      l1: ["tour"],
      comment: "NO is steady; EN often has sequence/r-colouring",
      easyComment:
        "Norwegian /ʉː/ is steady; don’t add English ‘r’ colouring or extra glide.",
      userComment:
        "Norwegian long /ʉː/ is a steady vowel. English ‘tour’ often has a vowel sequence or r-colouring depending on accent. Keep it steady in Norwegian.",
    },

    u: {
      overlap: "-",
      no: ["bodd"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["short-u", "rounded", "no-1-to-1-english"],
      easyComment:
        "NEW-ish: Norwegian short /u/ doesn’t line up neatly with one English vowel.",
      userComment:
        "Short /u/ in Norwegian is marked as no close English equivalent in this table. Many English speakers drift toward /ʊ/ or /ə/. Keep it clearly rounded and short; tune by listening to Norwegian minimal pairs.",
    },

    "uː": {
      overlap: "-",
      no: ["bod"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["long-u", "steady-vowel", "no-english-equivalent-table"],
      easyComment:
        "NEW-ish: Norwegian /uː/ is a steady long vowel (avoid glides).",
      userComment:
        "Long /uː/ target is marked as no close English equivalent in this table. Keep it long and steady, with strong lip rounding. Avoid turning it into a diphthong-like movement.",
    },

    o: {
      overlap: "~",
      no: ["dott"],
      l1: ["dot"],
      comment: "English ‘dot’ quality varies a lot by accent",
      easyComment:
        "Approximate match; English ‘dot’ varies, Norwegian target is steadier.",
      userComment:
        "Mid/back-ish rounded vowel. English ‘dot’ varies by accent (often more open). Treat as an approximate match and tune by listening.",
    },

    "oː": {
      overlap: "~",
      no: ["lån"],
      l1: ["lawn"],
      comment: "English may be more open or merged; NO aims steadier",
      easyComment:
        "Long rounded vowel; keep it steady (no English-style glide/merge).",
      userComment:
        "Long /oː/-like vowel. English ‘lawn’ can be more open or merged in some accents. Norwegian aims for a steadier long vowel.",
    },

    // -------------------------------- Diphthongs -------------------------------

    "ɑi": {
      overlap: "+",
      no: ["hai"],
      l1: ["hi"],
      comment: "",
      easyComment: "Good match with English “hi”.",
      userComment:
        "Diphthong similar to English ‘hi’. Good match.",
    },

    "æi": {
      overlap: "-",
      no: ["hei"],
      l1: [],
      comment: "PDF writes /ei/; normalized to canonical /æi/.",
      difficulty: "new",
      keywords: ["ei-diphthong", "start-open", "no-direct-english"],
      easyComment:
        "NEW: Norwegian ‘ei’ — starts more open than English ‘ay’ for many Brits.",
      userComment:
        "Norwegian ‘ei’ diphthong (often written <ei>). The start is typically more open/front than many English ‘FACE’ realisations. Keep it a clear 2-part movement, not an English-style ‘ay’ that may start higher.",
    },

    "øy": {
      overlap: "-",
      no: ["høy"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["front-rounded-diphthong", "øy", "no-direct-english"],
      easyComment:
        "NEW: /øy/ = rounded + front movement (no English equivalent).",
      userComment:
        "Norwegian diphthong with front rounding. No direct English equivalent. Keep lips rounded while the tongue moves; don’t drop rounding mid-way.",
    },

    "æʉ": {
      overlap: "-",
      no: ["sau"],
      l1: [],
      comment: "",
      difficulty: "new",
      keywords: ["au-diphthong", "æʉ", "no-direct-english"],
      easyComment:
        "NEW: Norwegian ‘au’ — a different glide than English ‘ow’.",
      userComment:
        "Norwegian ‘au’ diphthong. No direct English equivalent in the table. Often it doesn’t match English GOAT (‘oh’) movement; keep the Norwegian start more open and the rounding controlled through the glide.",
    },

    oy: {
      overlap: "+",
      no: ["soya"],
      l1: ["soya"],
      comment: "",
      easyComment: "Good match with English “soy”.",
      userComment:
        "Diphthong similar to English ‘soy(a)’. Good match.",
    },

    uy: {
      overlap: "-",
      no: ["hoie"],
      l1: [],
      comment: "Rare in Norwegian",
      difficulty: "new",
      keywords: ["rare", "diphthong", "uy"],
      easyComment: "Rare pattern; don’t worry early on.",
      userComment:
        "Rare diphthong pattern (marked rare in Norwegian). No close English equivalent.",
    },

    "ʉy": {
      overlap: "-",
      no: ["huie"],
      l1: [],
      comment: "Rare in Norwegian",
      difficulty: "new",
      keywords: ["rare", "diphthong", "ʉy"],
      easyComment: "Rare pattern; don’t worry early on.",
      userComment:
        "Rare diphthong pattern (marked rare in Norwegian). No close English equivalent.",
    },
  },
};
